/**
 * GenericTabelaPage — componente reutilizável para Tabelas Básicas padrão ThorJava
 * Refatorado com o Galatea Signature GridArchetype (Rule 31 / P0).
 * 100% dos estilos derivam da fonte soberana SignatureTokens.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Pencil, RefreshCw, Save, X, XCircle, Loader2, Download, Search } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { exportToExcel } from '../../utils/exportToExcel'
import { GridArchetype } from '../archetypes/GridArchetype'
import { useSignature } from '../../contexts/SignatureContext'

export interface TabelaRow {
  cd: string
  nm: string
  status: string
}

export interface TabelaEntityConfig {
  titulo: string
  breadcrumb?: string
  icon?: React.ReactNode
  cdMaxLen?: number
  nmMaxLen?: number
  api: {
    list: (empresaId: number, skip?: number, limit?: number) => Promise<TabelaRow[]>
    create: (empresaId: number, cd: string, nm: string, status: string) => Promise<TabelaRow>
    update: (empresaId: number, cd: string, nm?: string, status?: string) => Promise<TabelaRow>
  }
}

type ToastType = 'success' | 'error' | 'warning'
interface Toast { id: number; msg: string; type: ToastType }

const GenericTabelaPage: React.FC<TabelaEntityConfig> = ({
  titulo, breadcrumb = 'Tabelas › Básicas', icon: _icon, cdMaxLen = 4, nmMaxLen = 100, api
}) => {
  const { getEmpresaId, getEmpresaNome } = useAuth()
  const empresaId = getEmpresaId()
  const empresaNome = getEmpresaNome()
  const { t } = useTranslation()
  const { colors } = useSignature()

  const [rows, setRows] = useState<TabelaRow[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [editMode, setEditMode] = useState<'none' | 'new' | 'edit'>('none')
  const [form, setForm] = useState({ cd: '', nm: '', status: 'S' })
  const [saving, setSaving] = useState(false)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [search, setSearch] = useState('')
  const toastId = useRef(0)

  const toast = useCallback((msg: string, type: ToastType = 'success') => {
    const id = ++toastId.current
    setToasts(t => [...t, { id, msg, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500)
  }, [])

  const load = useCallback(async () => {
    if (!empresaId) { toast('Nenhuma empresa selecionada', 'error'); return }
    setLoading(true)
    try {
      const data = await api.list(empresaId)
      setRows(data)
    } catch (e: any) {
      toast(e.message || 'Erro ao carregar dados', 'error')
    } finally {
      setLoading(false)
    }
  }, [empresaId, api, toast])

  useEffect(() => { load() }, [load])

  const handleNew = () => {
    setSelected(null)
    setForm({ cd: '', nm: '', status: 'S' })
    setEditMode('new')
  }

  const handleEdit = () => {
    if (!selected) return
    const row = rows.find(r => r.cd === selected)
    if (!row) return
    setForm({ cd: row.cd, nm: row.nm, status: row.status })
    setEditMode('edit')
  }

  const handleCancel = () => {
    setEditMode('none')
    setForm({ cd: '', nm: '', status: 'S' })
  }

  const handleSave = async () => {
    if (!empresaId) return
    if (!form.cd.trim() || !form.nm.trim()) {
      toast('Código e Nome são obrigatórios', 'warning')
      return
    }
    setSaving(true)
    try {
      if (editMode === 'new') {
        const created = await api.create(empresaId, form.cd.trim().toUpperCase(), form.nm.trim(), form.status)
        setRows(prev => [...prev, created])
        setSelected(created.cd)
        toast('Registro criado com sucesso', 'success')
      } else {
        const updated = await api.update(empresaId, form.cd, form.nm.trim(), form.status)
        setRows(prev => prev.map(r => r.cd === form.cd ? updated : r))
        toast('Registro atualizado com sucesso', 'success')
      }
      setEditMode('none')
    } catch (e: any) {
      toast(e.message || 'Erro ao salvar', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleInativar = async () => {
    if (!empresaId || !selected) return
    try {
      const updated = await api.update(empresaId, selected, undefined, 'N')
      setRows(prev => prev.map(r => r.cd === selected ? updated : r))
      toast('Registro inativado com sucesso', 'warning')
    } catch (e: any) {
      toast(e.message || 'Erro ao inativar', 'error')
    }
  }

  const filteredRows = rows.filter(r =>
    r.cd.toLowerCase().includes(search.toLowerCase()) ||
    r.nm.toLowerCase().includes(search.toLowerCase())
  )

  // Exportar para Excel (xlsx)
  const handleExportExcel = () => {
    const columns = [
      { key: 'cd', label: 'Código' },
      { key: 'nm', label: 'Descrição' },
      { key: 'status', label: 'Status' }
    ]
    const dataRows = filteredRows.map(row => ({
      cd: row.cd,
      nm: row.nm,
      status: row.status === 'S' ? 'Ativo' : 'Inativo'
    }))
    
    exportToExcel(dataRows, columns, {
      filename: `${titulo.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: titulo
    })
    toast('Dados exportados para Excel!')
  }

  const selectedRow = rows.find(r => r.cd === selected)
  const selectedIsInativo = selectedRow?.status === 'N'

  return (
    <GridArchetype
      title={titulo}
      subtitle={`Empresa: ${empresaNome || 'Ativa'} · ${breadcrumb}`}
      gridActions={
        <div className="flex items-center gap-2">
          <button 
            onClick={handleExportExcel}
            disabled={filteredRows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-40 text-white rounded text-xs font-bold transition-colors cursor-pointer shadow-xs"
            style={{ backgroundColor: colors.success }}
            title="Exportar para Excel (.xlsx)"
          >
            <Download size={14} /> Excel
          </button>
          <span className="text-xs font-mono ml-2" style={{ color: colors.textSecondary }}>
            {loading ? t('common.loading') : `${filteredRows.length} de ${rows.length} registros`}
          </span>
        </div>
      }
      filterPanel={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Botão Novo - Token Success */}
            <button
              onClick={handleNew}
              disabled={editMode !== 'none'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
              style={{ backgroundColor: colors.success }}
            >
              <Plus size={14} /> {t('common.new', 'Novo')}
            </button>

            {/* Botão Editar - Token Primary */}
            <button
              onClick={handleEdit}
              disabled={!selected || editMode !== 'none'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
              style={{ backgroundColor: colors.primary }}
            >
              <Pencil size={14} /> {t('common.edit', 'Editar')}
            </button>

            {/* Botão Inativar - Token Danger */}
            <button
              onClick={handleInativar}
              disabled={!selected || editMode !== 'none' || selectedIsInativo}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
              style={{ backgroundColor: colors.danger }}
            >
              <XCircle size={14} /> Inativar
            </button>
          </div>

          <div className="flex items-center gap-2 flex-1 justify-end max-w-md">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
              <input
                type="text"
                placeholder={t('common.search') + '...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-xl border text-xs focus:outline-none"
                style={{
                  backgroundColor: colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              />
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="p-1.5 rounded-xl border text-xs font-bold transition-colors cursor-pointer"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
              title="Recarregar dados"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      }
    >
      {/* Form Inline quando em Edição */}
      {editMode !== 'none' && (
        <div
          className="p-4 border-b flex items-end gap-4 shrink-0"
          style={{
            backgroundColor: colors.primaryLight,
            borderColor: colors.border,
          }}
        >
          <div>
            <label className="block text-xs font-bold mb-1" style={{ color: colors.textSecondary }}>
              Código *
            </label>
            <input
              type="text"
              maxLength={cdMaxLen}
              disabled={editMode === 'edit'}
              value={form.cd}
              onChange={e => setForm(f => ({ ...f, cd: e.target.value.toUpperCase() }))}
              placeholder={`MAX${cdMaxLen}`}
              className="w-24 px-3 py-1.5 rounded-xl border text-xs font-mono uppercase focus:outline-none"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            />
          </div>
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-bold mb-1" style={{ color: colors.textSecondary }}>
              Descrição *
            </label>
            <input
              type="text"
              maxLength={nmMaxLen}
              value={form.nm}
              onChange={e => setForm(f => ({ ...f, nm: e.target.value }))}
              placeholder="Nome/Descrição"
              className="w-full px-3 py-1.5 rounded-xl border text-xs focus:outline-none"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-bold text-white cursor-pointer"
              style={{ backgroundColor: colors.success }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-bold border cursor-pointer"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            >
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Tabela de Dados */}
      <div className="overflow-y-auto flex-1 scrollbar-standard">
        <table className="w-full text-xs text-left">
          <thead
            className="uppercase text-[11px] font-bold tracking-wider sticky top-0 border-b z-10"
            style={{
              backgroundColor: colors.bgApp,
              borderColor: colors.border,
              color: colors.textSecondary,
            }}
          >
            <tr>
              <th className="px-4 py-3 w-28">Código</th>
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3 w-28 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: colors.border }}>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center font-semibold" style={{ color: colors.textMuted }}>
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              filteredRows.map(row => {
                const isSel = selected === row.cd
                const isInactive = row.status === 'N'

                return (
                  <tr
                    key={row.cd}
                    onClick={() => setSelected(row.cd)}
                    className={`cursor-pointer transition-all hover:bg-blue-500/10 ${
                      isSel ? 'border-l-4 border-l-blue-500 bg-blue-500/15 font-semibold' : ''
                    }`}
                    style={{
                      backgroundColor: isSel ? undefined : colors.bgSurface,
                      color: colors.textPrimary,
                    }}
                  >
                    <td className="px-4 py-3 font-mono font-bold tracking-wider text-slate-300 dark:text-slate-200">
                      {row.cd}
                    </td>
                    <td className={`px-4 py-3 ${isInactive ? 'line-through opacity-60' : ''}`}>
                      {row.nm}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-2xs ${
                          isInactive
                            ? 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                            : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isInactive ? 'bg-rose-400' : 'bg-emerald-400 animate-pulse'}`} />
                        {isInactive ? 'INATIVO' : 'ATIVO'}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Table Footer Bar (Padrão Galatea Gold) */}
      <div
        className="px-4 py-2 border-t flex items-center justify-between text-xs font-mono shrink-0"
        style={{
          backgroundColor: colors.bgApp,
          borderColor: colors.border,
          color: colors.textSecondary,
        }}
      >
        <span>
          {loading ? t('common.loading') : `Exibindo ${filteredRows.length} de ${rows.length} registros`}
        </span>
        {selected && (
          <span className="text-blue-400 font-semibold">
            Registro selecionado: <code className="font-bold">{selected}</code>
          </span>
        )}
      </div>

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map(t => (
            <div
              key={t.id}
              className="px-4 py-2.5 rounded-xl shadow-lg text-xs font-bold text-white border"
              style={{
                backgroundColor: t.type === 'error' ? colors.danger : colors.success,
                borderColor: colors.border,
              }}
            >
              {t.msg}
            </div>
          ))}
        </div>
      )}
    </GridArchetype>
  )
}

export default GenericTabelaPage
